<template>
  <transition name="popup-fade">
    <div class="popup-backdrop" @click="onClose">
      <div v-if="ticketLinesToDisplay.length" v-show="showCart" class="popup" @click.stop>
        <em class="fa fa-window-close" :style="closeIconStyle" @click="onClose"/>
        <span :style="normalTextStyle" class="title-props">{{ translatedTitle }}</span>
        <div v-if="mismatchError" class="mismatchDataError">
          <span>{{ translatedPaymentMismatchErrorText }}</span>
        </div>
        <hr class="separator"/>
        <div class="cart-container">
          <cart-row-block v-for="cartLine in ticketLinesToDisplay" :key="cartLine.product_guid"
                          :cart-line="cartLine" :text-style="normalTextStyle"
                          :unit-text="cartBreakdownProps.unitText"
                          :quantity-text-props="cartBreakdownProps.quantityTextProps"
                          :quantity-block-props="cartBreakdownProps.quantityBlockProps"
                          class="cart-row-container"/>
        </div>
        <div class="total-price-container">
          <hr class="separator"/>
          <div class="total-price-sub-container" v-if="ticketTotalPaid > 0.0">
            <div>
              <span :style="normalTextStyle" class="title-props">{{ totalPaidTextToDisplay }}{{ totalPaidToDisplay }}</span>
            </div>
          </div>
          <div class="total-price-sub-container">
            <div>
              <span :style="normalTextStyle" class="title-props">{{ totalTextToDisplay }}{{ totalPriceToDisplay }}</span>
            </div>
            <button @click="onPay()" :style="payIconStyle" class="title-props">{{ translatedPayText }}</button>
          </div>
        </div>
      </div>
      <div v-if="status" class="popup">
        <em class="fa fa-window-close" :style="closeIconStyle" @click="onClose"/>
        <span :class="statusClass">{{ statusText }}</span>
      </div>
      <div v-if="showEmptyCart" class="popup">
        <em class="fa fa-window-close" :style="closeIconStyle" @click="onClose"/>
        <span :style="textStyle">{{ translatedEmptyCartText }}</span>
      </div>
      <div v-if="isCartLoading" class="popup">
        <em class="fa fa-window-close" :style="closeIconStyle" @click="onClose"/>
        <span :style="textStyle">{{ translatedPaymentLoadingText }}</span>
        <i class="fa fa-spinner fa-spin spinner"/>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex';
import { MultiLanguageText, BlockProps, TextProps } from '../models';
import CartRowBlock from './CartRowBlock.vue';

export default {
  name: 'cart-popup-block',
  components: { CartRowBlock },
  props: {
    cartBreakdownProps: { type: Object, default: () => null },
    status: { type: String }
  },
  data() {
    return {
      paymentDataError: null,
      paymentError: null,
      mismatchError: null,
      isLoading: true,
    };
  },
  async mounted() {
    await this.getTicketLines();
    this.isLoading = false;
    this.initializeActiveWaiter();
  },
  destroyed() {
    this.destroyActiveWaiter();
  },
  computed: {
    ...mapGetters(['selectedLanguage']),
    isCartEmpty() {
      return this.ticketLinesToDisplay.length < 1 && !this.isCartLoading;
    },
    showEmptyCart() {
      return this.isCartEmpty && !this.isLoading;
    },
    isCartLoading() {
      return !this.ticket || this.ticket.generationStatus === 'GENERATING';
    },
    showCart() {
      return !this.status;
    },
    ticket() {
      return Object.keys(this.$store.getters.ticket).length > 0 ? this.$store.getters.ticket : null;
    },
    subdomain() {
      return this.$store.getters.subdomain;
    },
    saleLocationGuid() {
      return this.$store.getters.saleLocationGuid;
    },
    ticketLinesToDisplay() {
      return this.ticket?.lines || [];
    },
    ticketTotalPaid() {
      return this.ticket
        ? this.ticket.payments.reduce((acc, payment) => acc + payment.amount, 0.0) : 0.0;
    },
    ticketTotalPrice() {
      return this.ticket ? this.ticket.amount - this.ticketTotalPaid : 0.0;
    },
    translatedTitle() {
      return this.translatedCartProperty('title');
    },
    translatedEmptyCartText() {
      return this.translatedCartProperty('emptyCartText');
    },
    translatedTotalText() {
      return this.translatedCartProperty('totalText');
    },
    translatedPaidText() {
      return this.translatedCartProperty('paidText');
    },
    translatedPayText() {
      return this.translatedCartProperty('payText');
    },
    translatedPaymentDataErrorText() {
      return this.translatedCartProperty('paymentDataErrorText');
    },
    translatedPaymentMismatchErrorText() {
      return this.translatedCartProperty('paymentMismatchErrorText');
    },
    translatedPaymentErrorText() {
      return this.translatedCartProperty('paymentErrorText');
    },
    translatedPaymentSuccessText() {
      return this.translatedCartProperty('paymentSuccessText');
    },
    translatedPaymentLoadingText() {
      return this.translatedCartProperty('paymentLoadingText');
    },
    totalTextToDisplay() {
      return `${this.translatedTotalText}`;
    },
    totalPaidTextToDisplay() {
      return `${this.translatedPaidText}`;
    },
    totalPriceToDisplay() {
      return `${this.ticketTotalPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}€`;
    },
    totalPaidToDisplay() {
      return `${this.ticketTotalPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })}€`;
    },
    textStyle() {
      const textProperties = new TextProps(this.cartBreakdownProps.textProps);
      return textProperties.getStyles();
    },
    normalTextStyle() {
      const textProperties = new TextProps(this.cartBreakdownProps.textProps);
      return { ...textProperties.getStyles(), ...({ color: 'black' }) };
    },
    payIconStyle() {
      const payTextBlockProperties = new BlockProps(this.cartBreakdownProps.payProps);
      return { ...this.textStyle, ...payTextBlockProperties.getStyles() };
    },
    closeIconStyle() {
      const closeIconBlockProperties = new BlockProps(this.cartBreakdownProps.closeBlockProps);
      const closeIconTextProperties = new TextProps(this.cartBreakdownProps.closeTextProps);
      return { ...closeIconBlockProperties.getStyles(), ...closeIconTextProperties.getStyles() };
    },
    statusClass() {
      if (this.status) {
        if (this.status === 'paymentSuccess') return 'statusSuccess';
        return 'statusError';
      }
      return '';
    },
    statusText() {
      if (this.status) {
        if (this.status === 'paymentSuccess') return this.translatedPaymentSuccessText;
        return this.translatedPaymentErrorText;
      }
      return '';
    }
  },
  watch: {},
  methods: {
    translatedCartProperty(prop) {
      const property = this.cartBreakdownProps[prop];
      const localizedProperty = new MultiLanguageText(property);
      return localizedProperty.getText(this.selectedLanguage);
    },
    async clearStatus() {
      await this.$store.dispatch('setStatus', { status: '' });
    },
    async onClose() {
      await this.clearStatus();
      this.$emit('close');
    },
    onPay() {
      this.$root.$emit('show-payment', this.ticketTotalPrice);
      this.$emit('close');
    },
    async getTicketLines() {
      this.$root.$emit('get-ticket-lines');
    },
    initializeActiveWaiter() {
      this.$root.$emit('initialize-cart-active-waiter');
    },
    destroyActiveWaiter() {
      this.$root.$emit('destroy-cart-active-waiter');
    }
  }
};
</script>

<style lang="scss" scoped>
.popup-backdrop {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
}

.popup {
  background-color: #FFFFFF;
  box-shadow: rgba(0, 0, 0, 0.29) 5px 5px 25px 0;
  border-radius: 25px;
  overflow-x: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 85%;
  max-width: 450px;
  padding: 20px 20px 30px 20px;
  position: relative;
  height: fit-content;
  max-height: 75%;

  div.cart-row-container {
    margin-top: 5px;
    margin-bottom: 25px;
  }

  div.cart-container {
    width: 100%;
    box-sizing: border-box;
    margin-top: 15px;
    padding-right: 25px;
    padding-left: 25px;
    overflow-y: scroll;
    /* https://stackoverflow.com/a/34299947 show user scroll on mobile by using shadow */
    background:
    /* Shadow covers */
    linear-gradient(white 30%, rgba(255,255,255,0)),
    linear-gradient(rgba(255,255,255,0), white 70%) 0 100%,

    /* Shadows */
    radial-gradient(farthest-side at 50% 0, rgba(0,0,0,.2), rgba(0,0,0,0)),
    radial-gradient(farthest-side at 50% 100%, rgba(0,0,0,.2), rgba(0,0,0,0)) 0 100%;
    background-repeat: no-repeat;
    background-color: white;
    background-size: 100% 40px, 100% 40px, 100% 14px, 100% 14px;

    /* Opera doesn't support this in the shorthand */
    background-attachment: local, local, scroll, scroll;
  }

  // A numeric drawing rule expected second to last element to deleting its margin
  div:nth-last-child(2) {
    margin-bottom: 0;
  }
}

.separator {
  width: 85%;
  border: solid 1px;
}

.total-price-container {
  align-self: end;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 15px;
  width: 100%;
}

.total-price-sub-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-top: 15px;
  width: 85%;
}

.title-props {
  font-weight: bold;
}

.statusError {
  color: red;
}

.statusSuccess {
  color: green;
}
</style>
